<template>
  <div class="bx-header__search bx-typo--header-search">
    <button aria-label="Suche öffnen"
            class="bx-header__search-toggle icon icon-search"
            @click="toggleSearch()"
            @keyup.enter="toggleSearch()">
      <svg><use href="/images/icon_sprite.svg#search" /></svg>
    </button>

    <form class="bx-form bx-form--search-header bx-typo--form-search"
          onsubmit="return false;"
          @submit="submitSearch()">
      <div class="bx-header__search-wrapper">
        <div class="bx-searchfield">
          <input id="bx-header-search-input"
                 ref="searchInput"
                 v-model="searchPhrase"
                 type="search"
                 class="bx-form__input"
                 :title="placeholder"
                 :placeholder="placeholder"
                 :autocomplete="isEpgSearch ? 'off' : 'on'"
                 @keyup.enter.prevent="submitSearch()"
                 @input="isEpgSearch ? triggerEpgAutocompleteSearch() : null">
          <button aria-label="Suchen"
                  class="bx-form__search-submit icon icon-search"
                  @click="submitSearch()"
                  @keyup.enter="submitSearch()">
            <svg><use href="/images/icon_sprite.svg#search" /></svg>
          </button>
        </div>
        <div class="bx-form__search-tabs-wrapper">
          <div class="bx-form__search-tabs">
            <button v-for="type in searchTypes"
                    :key="type.value"
                    :data-search-type="type.value"
                    type="button"
                    :class="['bx-form__search-tab bx-typo__search-tab', { 'is-active': searchType === type.value }]"
                    @click="searchType = type.value,
                            setFocus(),
                            isEpgSearch && searchPhrase ? triggerEpgAutocompleteSearch() : null">
              {{ type.label }}
            </button>
          </div>
        </div>
      </div>

      <button aria-label="Suche schließen"
              type="button"
              class="bx-header__search-close icon icon-close"
              @click="toggleSearch()"
              @keyup.enter="toggleSearch()">
        <svg><use href="/images/icon_sprite.svg#close" /></svg>
      </button>

      <EpgAutocompleteFlyout v-if="isEpgSearch && searchPhrase"
                             :search-phrase="searchPhrase"
                             :epg-autocomplete-search="epgAutocompleteSearch"
                             :has-api-error="hasApiError"
                             :search-in-progress="searchInProgress"
                             @close-flyout="cleanupEpgAutocompleteSearch()" />
    </form>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'pinia'

import { useConfigStore } from '../../../stores/config'
import { cleanUpSearchPhrase } from '../../../utils/strings.ts'
import EpgAutocompleteFlyout from './EpgSearch/EpgAutocompleteFlyout.vue'

export default {
  components: {
    EpgAutocompleteFlyout
  },
  data () {
    return {
      observer: null,
      hasDetachedHeader: true,
      hasStickyHeader: false,
      hasApiError: false,
      searchInProgress: false,
      searchPhrase: '',
      showSearch: false,
      searchType: 'epg', // ToDo: Prevent fallback to default after submiting search
      searchTypes: [
        { value: 'epg', label: 'TV Programm' },
        { value: 'wer-streamt', label: 'Streaming' },
        { value: 'suche', label: 'News' }
      ],
      epgAutocompleteSearch: {
        channels: [],
        broadcasts: {} // see `EpgAutocompleteSearchResult` type
      },
      debounceTimeoutId: null,
      body: null
    }
  },
  computed: {
    ...mapState(useConfigStore, ['rsConfig']),
    placeholder () {
      if (this.searchType === 'wer-streamt') {
        return 'Nach Streaming-Titeln suchen'
      } else if (this.isEpgSearch) {
        return 'Sender und Sendungen suchen'
      } else {
        return 'Nach TV-News suchen'
      }
    },
    isEpgSearch () {
      return this.searchType === 'epg'
    }
  },
  mounted () {
    this.body = document.body
    this.headerStateObserver()
  },
  beforeUnmount () {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    headerStateObserver () {
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            const isStickyHeader = this.body.classList.contains('bx-js-sticky-header')
            const isDetachedHeader = this.body.classList.contains('bx-js-detach-fixed-header')

            if (isStickyHeader && !this.hasStickyHeader) {
              this.hasStickyHeader = true
            } else if (!isStickyHeader && this.hasStickyHeader) {
              this.hasStickyHeader = false
              this.cleanupEpgAutocompleteSearch()
            } else if (!isDetachedHeader && !this.hasStickyHeader && this.hasDetachedHeader) {
              this.hasDetachedHeader = false
              this.cleanupEpgAutocompleteSearch()
            } else if (isDetachedHeader && !this.hasStickyHeader && !this.hasDetachedHeader) {
              this.hasDetachedHeader = true
            }
          }
        })
      })

      this.observer.observe(this.body, {
        attributes: true,
        attributeFilter: ['class']
      })
    },
    cleanupEpgAutocompleteSearch () {
      this.searchPhrase = ''
      this.resetEpgAutocompleteSearchResults()
      if (this.showSearch) {
        this.toggleSearch()
      }
    },
    toggleSearch () {
      this.showSearch = this.body.classList.toggle('bx-js-show-search')
      if (this.showSearch) {
        this.setFocus()
      }
    },
    setFocus () {
      this.$refs.searchInput.focus()
    },
    submitSearch () {
      if (!this.isEpgSearch) {
        const url = `/${this.searchType}?query=${encodeURIComponent(this.$refs.searchInput.value.trim())}`
        if (this.searchType === 'wer-streamt') {
          window.location.href = `${url}#search-filters`
        } else {
          window.location.href = `${url}`
        }
      }
    },
    triggerEpgAutocompleteSearch () {
      if (this.searchPhrase) {
        clearTimeout(this.debounceTimeoutId)
        this.debounceTimeoutId = setTimeout(this.performEpgAutocompleteSearch, 250) // be kind to the server
      } else {
        this.resetEpgAutocompleteSearchResults()
      }
    },
    async performEpgAutocompleteSearch () {
      this.searchInProgress = true
      this.hasApiError = false

      try {
        const url = this.rsConfig.epgAutocompleteSearchServiceUrl
        const params = { phrase: cleanUpSearchPhrase(this.searchPhrase) } // no schmutz in composer
        const { data } = await axios.get(url, { params }) || {}

        this.epgAutocompleteSearch = {
          channels: data.channels || [],
          broadcasts: data.broadcasts || {}
        }
      } catch (error) {
        this.epgAutocompleteSearch = {
          channels: [],
          broadcasts: {}
        }
        this.hasApiError = true
        console.error('Error fetching EPG autocomplete search results:', error)
      }
      this.searchInProgress = false
    },
    resetEpgAutocompleteSearchResults () {
      this.epgAutocompleteSearch = {
        channels: [],
        broadcasts: {}
      }
    }
  }
}
</script>
